import React, { useState } from 'react';
import { FiChevronDown, FiDatabase, FiFilter, FiSearch } from 'react-icons/fi';

const categories = [
  {
    title: 'Data Retrieval',
    icon: FiDatabase,
    methods: [
      { name: 'getProvinces()', returnType: 'string[]', description: 'Get all provinces' },
      { name: 'getDistricts()', returnType: 'string[]', description: 'Get all districts' },
      { name: 'getCities()', returnType: 'string[]', description: 'Get all city names' },
      { name: 'getPostals()', returnType: 'PostalRecord[]', description: 'Get the complete dataset' },
    ],
  },
  {
    title: 'Lookup & Filtering',
    icon: FiFilter,
    methods: [
      { name: 'getDistrictsByProvince(province)', returnType: 'string[]', description: 'Districts within a province' },
      { name: 'getCitiesByDistrict(district)', returnType: 'string[]', description: 'Cities within a district' },
      { name: 'getCitiesByProvince(province)', returnType: 'string[]', description: 'All cities in a province' },
      { name: 'getInfoByPostal(code)', returnType: 'PostalRecord | null', description: 'Full info for a postal code' },
      { name: 'getInfoByCity(city)', returnType: 'PostalRecord[]', description: 'All records for a city name' },
      { name: 'getDistrictByPostal(code)', returnType: 'string | null', description: 'District for a postal code' },
      { name: 'getProvinceByPostal(code)', returnType: 'string | null', description: 'Province for a postal code' },
      { name: 'getCityByPostal(code)', returnType: 'string | null', description: 'City name for a postal code' },
      { name: 'isValidPostal(code)', returnType: 'boolean', description: 'Check if a postal code exists' },
      { name: 'getPostalsByCity(city)', returnType: 'PostalRecord[]', description: 'All postal records for a city' },
      { name: 'getPostalsByDistrict(district)', returnType: 'PostalRecord[]', description: 'All postal records in a district' },
      { name: 'getPostalsByProvince(province)', returnType: 'Record<string, PostalRecord[]>', description: 'All postal records grouped by district' },
    ],
  },
  {
    title: 'Search & Transform',
    icon: FiSearch,
    methods: [
      { name: 'searchCity(query, limit?)', returnType: 'CitySearchResult[]', description: 'Fuzzy search with scored results' },
      { name: 'autocompleteCity(input, limit?)', returnType: 'string[]', description: 'Prefix-based auto-complete' },
      { name: 'normalize(input)', returnType: 'string', description: 'Normalize messy city names' },
      { name: 'toReadable(code)', returnType: 'string | null', description: 'Human-readable format: City, District, Province' },
    ],
  },
];

function ApiReference() {
  const [expanded, setExpanded] = useState(() =>
    categories.reduce((acc, _, index) => ({ ...acc, [index]: true }), {})
  );

  const toggleCategory = (index) => {
    setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <section id="api" className="bg-white/[0.02] py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="section-intro text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold gradient-text mb-4">
            API Reference
          </h2>
          <p className="text-lg text-slate-400">
            Everything you need to work with Sri Lankan addresses
          </p>
        </div>

        <div className="space-y-6">
          {categories.map((category, catIndex) => {
            const Icon = category.icon;
            const isExpanded = expanded[catIndex];

            return (
              <div
                key={catIndex}
                className="api-category rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden"
              >
                <button
                  onClick={() => toggleCategory(catIndex)}
                  className="w-full bg-primary-500/10 p-5 flex justify-between items-center cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-primary-400" />
                    <span className="text-lg font-semibold text-primary-200">
                      {category.title}
                    </span>
                    <span className="text-sm text-slate-500">
                      ({category.methods.length} methods)
                    </span>
                  </div>
                  <FiChevronDown
                    className={`w-5 h-5 text-primary-400 transition-transform duration-200 ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isExpanded && (
                  <div>
                    {category.methods.map((method, methodIndex) => (
                      <div
                        key={methodIndex}
                        className={`p-4 flex items-start gap-4 ${
                          methodIndex < category.methods.length - 1
                            ? 'border-b border-white/[0.06]'
                            : ''
                        }`}
                      >
                        <code className="font-mono text-sm text-primary-400 whitespace-nowrap pt-0.5">
                          {method.name}
                        </code>
                        <span className="bg-accent-500/15 text-accent-400 rounded-full px-2 py-0.5 text-xs whitespace-nowrap">
                          {method.returnType}
                        </span>
                        <span className="text-slate-400 text-sm">
                          {method.description}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default ApiReference;
